import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import NewsletterSendReport from './sendReport';

@observer
class NewsletterSendDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedLists: {},
            fromEmail: props.fromEmail || '',
            sending: false,
            testing: false,
            alreadyRunning: false,
            message: '',
            error: '',
            counts: {},
            reportSendId: 0,
            showReport: false,
        };
    }

    componentDidMount() {
        const { recipientListStore } = this.props.stores;
        recipientListStore.loadLists();
    }

    toggleList = async (e) => {
        const { id } = e.currentTarget.dataset;
        const listId = parseInt(id, 10);
        const selectedLists = { ...this.state.selectedLists };
        const wasSelected = !!selectedLists[listId];
        if (wasSelected) {
            delete selectedLists[listId];
        } else {
            selectedLists[listId] = true;
        }
        this.setState({ selectedLists });

        // Only resolve on SELECT. Deselecting cannot change what the list resolves to, and
        // resolving walks the whole audience server-side — a round trip whose result is
        // immediately excluded from the total anyway.
        if (wasSelected) {
            return;
        }
        const { recipientListStore } = this.props.stores;
        const response = await recipientListStore.resolve(listId);
        if (response?.status === 200) {
            this.setState(prev => ({ counts: { ...prev.counts, [listId]: response.data } }));
        }
    }

    payload() {
        const {
            newsletterId, subject, bodyHtml, bodyText, hostname, users = [], adhocRecipients = [],
        } = this.props;
        return {
            newsletterId,
            lists: Object.keys(this.state.selectedLists).map(Number),
            users,
            adhocRecipients,
            subject,
            fromEmail: this.state.fromEmail,
            bodyHtml,
            bodyText,
            hostname,
        };
    }

    doTestSend = async () => {
        const { newsletterStore } = this.props.stores;
        this.setState({ testing: true, error: '', message: '' });
        const response = await newsletterStore.testSend(this.props.newsletterId, this.payload());
        if (response?.status === 200) {
            this.setState({ testing: false, message: `Testmail sendt til ${response.data?.to}` });
        } else {
            this.setState({ testing: false, error: response?.message || 'Kunne ikke sende testmail.' });
        }
    }

    doSend = async () => {
        const { appState, newsletterStore } = this.props.stores;
        this.setState({ sending: true, error: '', message: '' });
        const response = await newsletterStore.startSend(this.props.newsletterId, this.payload());
        if (response?.status === 202) {
            this.setState({ sending: false, message: 'Nyhetsbrevet er lagt i kø.' });
            // Close THIS drawer only. toggleDrawer() with no args defaults to level 1,
            // and level 1 force-closes 2-9 (appState.js:910-915), taking the builder with it.
            setTimeout(() => appState.toggleDrawer(false, this.props.drawerLevel), 1500);
        } else if (response?.status === 409) {
            // NOT a failure: the backend already has a running job for this newsletter, which
            // is usually this same admin's first click surfacing as an error because a proxy
            // timed out on a request that was in fact accepted. Say so plainly and keep the
            // button disabled — re-posting can only 409 again, and the thing 409 exists to
            // prevent is a second job mailing the whole audience twice.
            const sendId = response.data?.sendId;
            this.setState({
                sending: false,
                alreadyRunning: true,
                // Naming the blocking send is only useful if the admin can reach it, so the
                // id also arms the inline report below rather than being a dead reference.
                reportSendId: sendId > 0 ? sendId : 0,
                message: sendId > 0
                    ? `Utsending pågår allerede for dette nyhetsbrevet (utsending #${sendId}).`
                    : 'Utsending pågår allerede for dette nyhetsbrevet. Følg med i Rapport.',
            });
        } else {
            this.setState({ sending: false, error: response?.message || 'Kunne ikke starte utsending.' });
        }
    }

    handleFrom = (e) => {
        this.setState({ fromEmail: e.target.value });
    }

    showReport = () => {
        this.setState({ showReport: true });
    }

    render() {
        const { recipientListStore } = this.props.stores;
        const { users = [], adhocRecipients = [] } = this.props;
        const lists = recipientListStore.recipientLists || [];
        const {
            selectedLists, fromEmail, sending, testing, alreadyRunning, message, error, counts,
            reportSendId, showReport,
        } = this.state;

        const selectedIds = Object.keys(selectedLists).map(Number);
        const listTotal = selectedIds.reduce((sum, id) => sum + (counts[id]?.pending || 0), 0);
        const total = listTotal + users.length + adhocRecipients.length;
        // The route rejects an unusable from-address with 400 (an empty one would otherwise
        // fall back to an unverified SES identity and fail every single recipient), so do not
        // let the admin post a request that is certain to be refused.
        const fromEmailOk = util.validateEmail(fromEmail);

        return (
            <div class='d-flex flex-column overflow-auto mb-5 mt-3 p-3'>
                <div class='font-weight-lighter px-1 box-header mb-2'>
                    <i class='fa-solid fa-paper-plane me-2' />
                    <Text id='newsletter.send-title'>Send newsletter</Text>
                </div>

                <label class='form-label mt-2'>
                    <Text id='newsletter.from-email'>Sender address</Text>
                </label>
                <input
                    class={`form-control ${fromEmailOk ? '' : 'is-invalid'}`}
                    value={fromEmail}
                    onInput={this.handleFrom}
                    placeholder='post@example.no'
                />
                {!fromEmailOk && (
                    <div class='invalid-feedback d-block'>
                        <Text id='newsletter.from-email-invalid'>Enter a valid sender address before sending.</Text>
                    </div>
                )}

                <div class='font-weight-lighter px-1 box-header mt-4 mb-2'>
                    <Text id='newsletter.pick-lists'>Recipient lists</Text>
                </div>
                <div class='list-group'>
                    {lists.map(list => (
                        <button
                            key={list.id}
                            type='button'
                            class={`list-group-item list-group-item-action ${selectedLists[list.id] ? 'active' : ''}`}
                            data-id={list.id}
                            onClick={this.toggleList}
                        >
                            <div class='d-flex justify-content-between align-items-center'>
                                <strong>{list.name}</strong>
                                {counts[list.id]?.pending > 0 && (
                                    <span class='badge text-bg-secondary'>{counts[list.id].pending}</span>
                                )}
                            </div>
                            {!!list.description && <small class='d-block'>{list.description}</small>}
                        </button>
                    ))}
                </div>

                {/* An UPPER BOUND, not a count. Each list is resolved on its own, so a person
                    on two selected lists is counted twice here. The job dedupes for real
                    (by user id AND by email), so the true audience is only ever smaller —
                    labelled rather than faked, because deduping honestly on the client would
                    mean shipping every resolved row to the browser. */}
                <div class='alert alert-info rounded mt-3'>
                    {total > 0 ? (
                        <Text id='newsletter.total-recipients-approx' fields={{ total }}>
                            Up to {total} recipients
                        </Text>
                    ) : (
                        <Text id='newsletter.total-recipients' fields={{ total }}>
                            {total} recipients
                        </Text>
                    )}
                    {total > 0 && (
                        <small class='d-block text-body-secondary'>
                            <Text id='newsletter.total-recipients-note'>
                                People on more than one list are counted once when the newsletter is actually sent.
                            </Text>
                        </small>
                    )}
                </div>

                <button type='button' class='btn btn-outline-secondary rounded-pill mt-2' onClick={this.doTestSend} disabled={testing}>
                    <i class='fa-solid fa-flask me-1' /> <Text id='newsletter.test-send'>Send test to me</Text>
                    {testing && <span class='spinner-border spinner-border-sm ms-2' role='status' aria-hidden='true' />}
                </button>

                <button
                    type='button'
                    class='btn btn-primary rounded-pill mt-2'
                    onClick={this.doSend}
                    disabled={sending || alreadyRunning || total === 0 || !fromEmailOk}
                >
                    <i class='fa-solid fa-paper-plane me-1' /> <Text id='newsletter.send'>Send newsletter</Text>
                    {sending && <span class='spinner-border spinner-border-sm ms-2' role='status' aria-hidden='true' />}
                </button>

                {!!error && <div class='alert alert-danger rounded mt-3'>{error}</div>}
                {!!message && <div class='alert alert-success rounded mt-3'>{message}</div>}

                {alreadyRunning && reportSendId > 0 && !showReport && (
                    <button type='button' class='btn btn-outline-primary rounded-pill mt-2' onClick={this.showReport}>
                        <i class='fa-solid fa-chart-line me-1' />
                        <Text id='newsletter.open-report' fields={{ sendId: reportSendId }}>
                            Open the report for send #{reportSendId}
                        </Text>
                    </button>
                )}
                {showReport && reportSendId > 0 && (
                    <NewsletterSendReport stores={this.props.stores} sendId={reportSendId} />
                )}
            </div>
        );
    }
}

export default NewsletterSendDrawer;
